// @ts-nocheck
import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { SubSection, FieldLabel, inputClass } from "./SectionWrapper";
import { PayLaterSection } from "./PayLaterSection";
import { supabase } from "@/integrations/supabase/client";
import {
  EventData,
  PricingTier,
  DiscountPolicy,
  CatalogDivision,
  EventDivisionSettings,
} from "@/types/events";
import { cn } from "@/lib/utils";

interface Props {
  event: EventData;
  updateEvent: (updates: Partial<EventData>) => void;
  pricingTiers: PricingTier[];
  setPricingTiers: React.Dispatch<React.SetStateAction<PricingTier[]>>;
  discountPolicies: DiscountPolicy[];
  divisionSettings: EventDivisionSettings[];
  setDivisionSettings: React.Dispatch<React.SetStateAction<EventDivisionSettings[]>>;
  onTiersChanged?: () => void;
}

type FilterMode = "all" | "boys" | "girls" | "active";

interface MergedRow {
  catalog: CatalogDivision;
  settings: EventDivisionSettings | null;
}

function emptySettings(eventId: string, catalogDivisionId: string, displayOrder: number): EventDivisionSettings {
  return {
    id: "",
    event_id: eventId,
    catalog_division_id: catalogDivisionId,
    has_boys: false,
    has_girls: false,
    boys_price: 0,
    girls_price: 0,
    boys_max_teams: null,
    girls_max_teams: null,
    accepts_special_requests: false,
    special_request_notes: "",
    special_request_fee: 0,
    is_active: true,
    display_order: displayOrder,
  };
}

export function RegistrationSection({
  event,
  updateEvent,
  discountPolicies,
  divisionSettings,
  setDivisionSettings,
  onTiersChanged,
}: Props) {
  const [catalog, setCatalog] = useState<CatalogDivision[]>([]);
  const [loadingCatalog, setLoadingCatalog] = useState(false);
  const [filter, setFilter] = useState<FilterMode>("all");
  
  const [bulkBoysPrice, setBulkBoysPrice] = useState("");
  const [bulkGirlsPrice, setBulkGirlsPrice] = useState("");

  const sportId = event.sport_id;
  const orgId = event.organization_id;

  useEffect(() => {
    if (!sportId || !orgId) {
      setCatalog([]);
      return;
    }
    setLoadingCatalog(true);
    (async () => {
      const { data } = await supabase
        .from("division_catalog")
        .select("*")
        .eq("sport_id", sportId)
        .eq("organization_id", orgId)
        .eq("is_active", true)
        .order("display_order");
      setCatalog(data ?? []);
      setLoadingCatalog(false);
    })();
  }, [sportId, orgId]);

  const merged: MergedRow[] = useMemo(() => {
    return catalog.map((c) => ({
      catalog: c,
      settings: divisionSettings.find((d) => d.catalog_division_id === c.id) ?? null,
    }));
  }, [catalog, divisionSettings]);

  const filteredRows = useMemo(() => {
    return merged.filter(({ settings }) => {
      if (filter === "all") return true;
      if (filter === "boys") return !!settings?.has_boys;
      if (filter === "girls") return !!settings?.has_girls;
      if (filter === "active") return !!settings && (settings.has_boys || settings.has_girls);
      return true;
    });
  }, [merged, filter]);

  const updateRow = (catalogDivisionId: string, updates: Partial<EventDivisionSettings>) => {
    setDivisionSettings((prev) => {
      const idx = prev.findIndex((d) => d.catalog_division_id === catalogDivisionId);
      if (idx === -1) {
        const cat = catalog.find((c) => c.id === catalogDivisionId);
        const base = emptySettings(event.id || "", catalogDivisionId, cat?.display_order ?? 0);
        return [...prev, { ...base, ...updates }];
      }
      const next = [...prev];
      const merged = { ...next[idx], ...updates };
      // If both genders unchecked, mark inactive (but keep row to preserve pricing)
      if (!merged.has_boys && !merged.has_girls) {
        merged.is_active = false;
      } else {
        merged.is_active = true;
      }
      next[idx] = merged;
      return next;
    });
    onTiersChanged?.();
  };

  const applyBulkBoys = () => {
    const price = parseFloat(bulkBoysPrice);
    if (Number.isNaN(price)) return;
    setDivisionSettings((prev) =>
      prev.map((d) => (d.has_boys ? { ...d, boys_price: price } : d))
    );
    onTiersChanged?.();
  };

  const applyBulkGirls = () => {
    const price = parseFloat(bulkGirlsPrice);
    if (Number.isNaN(price)) return;
    setDivisionSettings((prev) =>
      prev.map((d) => (d.has_girls ? { ...d, girls_price: price } : d))
    );
    onTiersChanged?.();
  };

  const [waitlistCount, setWaitlistCount] = useState<number>(0);
  useEffect(() => {
    if (!event.id || !event.waitlist_enabled) return;
    (async () => {
      const { count } = await supabase
        .from("event_waitlist")
        .select("id", { count: "exact", head: true })
        .eq("event_id", event.id);
      setWaitlistCount(count ?? 0);
    })();
  }, [event.id, event.waitlist_enabled]);

  return (
    <div className="space-y-6">
      <SubSection title="Waitlist">
        <label className="flex items-center justify-between p-3 border border-border rounded-lg">
          <span className="text-sm font-medium">Enable waitlist for this event</span>
          <input
            type="checkbox"
            checked={!!event.waitlist_enabled}
            onChange={(e) => updateEvent({ waitlist_enabled: e.target.checked })}
            className="rounded"
          />
        </label>
        {event.waitlist_enabled && (
          <div className="space-y-3 mt-3">
            <div>
              <FieldLabel>Message shown to users joining the waitlist</FieldLabel>
              <textarea
                value={event.waitlist_message ?? ""}
                onChange={(e) => updateEvent({ waitlist_message: e.target.value })}
                placeholder="We'll notify you if a spot opens up. No payment is required to join the waitlist."
                rows={3}
                className={inputClass}
              />
            </div>
            {event.id && (
              <Link
                to={`/admin-dashboard/events/${event.id}/edit?step=waitlist`}
                className="text-xs text-primary hover:underline"
              >
                View waitlist ({waitlistCount} {waitlistCount === 1 ? "person" : "people"} waiting) →
              </Link>
            )}
          </div>
        )}
      </SubSection>

      <SubSection title="Pay Later (offline payments)">
        <div className="space-y-3">
          <label className="flex items-center justify-between p-3 border border-border rounded-lg">
            <span className="text-sm font-medium">Allow Pay Later</span>
            <input
              type="checkbox"
              checked={!!event.allow_pay_later}
              onChange={(e) => updateEvent({ allow_pay_later: e.target.checked })}
              className="rounded"
            />
          </label>

          {event.allow_pay_later && (
            <>
              <div className="flex flex-wrap gap-4">
                {["check", "venmo"].map((m) => {
                  const methods: string[] = (event.pay_later_methods ?? []).filter(
                    (x: string) => x !== "cash"
                  );
                  return (
                    <label key={m} className="flex items-center gap-2 text-sm capitalize">
                      <input
                        type="checkbox"
                        checked={methods.includes(m)}
                        onChange={(e) =>
                          updateEvent({
                            pay_later_methods: e.target.checked
                              ? [...methods, m]
                              : methods.filter((x) => x !== m),
                          })
                        }
                        className="rounded"
                      />
                      {m}
                    </label>
                  );
                })}
              </div>

              <div>
                <FieldLabel>Venmo handle</FieldLabel>
                <input
                  value={event.pay_later_venmo_handle ?? ""}
                  onChange={(e) => updateEvent({ pay_later_venmo_handle: e.target.value })}
                  placeholder="@mikejenright"
                  className={inputClass}
                />
              </div>

              <div>
                <FieldLabel>Venmo note (shown under the handle)</FieldLabel>
                <textarea
                  rows={2}
                  value={event.pay_later_venmo_note ?? ""}
                  onChange={(e) => updateEvent({ pay_later_venmo_note: e.target.value })}
                  placeholder="Please put the Team Name, Age Group, and Event Date that you are paying for on the Venmo payment."
                  className={inputClass}
                />
              </div>

              <div>
                <FieldLabel>Check instructions</FieldLabel>
                <textarea
                  rows={3}
                  value={event.pay_later_check_instructions ?? ""}
                  onChange={(e) => updateEvent({ pay_later_check_instructions: e.target.value })}
                  placeholder="Please contact the tournament director if you would like to pay by check. All checks must be received before the payment deadline."
                  className={inputClass}
                />
              </div>
            </>
          )}
        </div>
      </SubSection>

      <SubSection title="Registration Settings">

        <div className="space-y-3">
          <label className="flex items-center justify-between p-3 border border-border rounded-lg">
            <span className="text-sm font-medium">Accept Online Registrations</span>
            <input
              type="checkbox"
              checked={event.accept_online_registrations}
              onChange={(e) => updateEvent({ accept_online_registrations: e.target.checked })}
              className="rounded"
            />
          </label>

          <label className="flex items-center justify-between p-3 border border-border rounded-lg">
            <span className="text-sm font-medium">Allow Check-in</span>
            <input
              type="checkbox"
              checked={event.allow_checkin}
              onChange={(e) => updateEvent({ allow_checkin: e.target.checked })}
              className="rounded"
            />
          </label>

          <label className="flex items-center justify-between p-3 border border-border rounded-lg">
            <span className="text-sm font-medium">Allow Coupons</span>
            <input
              type="checkbox"
              checked={event.allow_coupons}
              onChange={(e) => updateEvent({ allow_coupons: e.target.checked })}
              className="rounded"
            />
          </label>

          <div>
            <FieldLabel>Assign Discount Policy</FieldLabel>
            <select
              value={event.discount_policy_id}
              onChange={(e) => updateEvent({ discount_policy_id: e.target.value })}
              className={inputClass}
            >
              <option value="">None</option>
              {discountPolicies.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <FieldLabel>External Registration URL</FieldLabel>
            <p className="text-xs text-muted-foreground mb-1">
              If provided, users will be redirected to this URL for registration instead of the
              built-in form.
            </p>
            <input
              type="url"
              value={event.external_registration_url}
              onChange={(e) => updateEvent({ external_registration_url: e.target.value })}
              placeholder="https://..."
              className={inputClass}
            />
          </div>
        </div>
      </SubSection>

      <PayLaterSection event={event} updateEvent={updateEvent} />

      <SubSection title="Registration & Pricing">
        <div>
          <FieldLabel>Registration Type</FieldLabel>
          <div className="flex gap-4">
            {["Individual", "Team"].map((type) => (
              <label key={type} className="flex items-center gap-2">
                <input
                  type="radio"
                  name="regType"
                  value={type}
                  checked={event.registration_type === type}
                  onChange={(e) => updateEvent({ registration_type: e.target.value })}
                  className="rounded"
                />
                <span className="text-sm">{type}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <FieldLabel>Roster Deadline</FieldLabel>
            <input
              type="date"
              value={event.roster_deadline?.split("T")[0] ?? ""}
              onChange={(e) => updateEvent({ roster_deadline: e.target.value })}
              className={inputClass}
            />
          </div>
          <div>
            <FieldLabel>Minimum Players Per Roster</FieldLabel>
            <input
              type="number"
              min={1}
              value={(event as any).roster_minimum_players ?? 6}
              onChange={(e) =>
                updateEvent({
                  roster_minimum_players:
                    e.target.value === "" ? "" : Number(e.target.value),
                } as any)
              }
              className={inputClass}
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Drives the Roster column on the Live Sheet. Teams below this after the
              deadline show as overdue.
            </p>
          </div>
        </div>
      </SubSection>

      <SubSection title="Pricing Tiers">
        <p className="text-xs text-muted-foreground -mt-2 mb-3">
          Check the divisions you're offering. Set boys/girls pricing per division.
        </p>

        {!sportId ? (
          <div className="p-4 border border-dashed border-border rounded-lg text-sm text-muted-foreground">
            Select a sport in Basic Info to configure divisions.
          </div>
        ) : loadingCatalog ? (
          <p className="text-sm text-muted-foreground">Loading divisions…</p>
        ) : catalog.length === 0 ? (
          <div className="p-4 border border-dashed border-border rounded-lg text-sm">
            No divisions in catalog for this sport.{" "}
            <Link to="/admin-dashboard/divisions" className="text-primary underline">
              Manage divisions in catalog →
            </Link>
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
              <div className="text-xs text-muted-foreground">
                {catalog.length} division{catalog.length !== 1 ? "s" : ""} available
              </div>
              <Link
                to="/admin-dashboard/divisions"
                className="text-xs text-primary hover:underline"
              >
                Manage divisions in catalog →
              </Link>
            </div>

            <div className="flex flex-wrap gap-2 mb-3">
              {(
                [
                  { v: "all", l: "Show all" },
                  { v: "boys", l: "Boys offered" },
                  { v: "girls", l: "Girls offered" },
                  { v: "active", l: "Active only" },
                ] as { v: FilterMode; l: string }[]
              ).map((opt) => (
                <button
                  key={opt.v}
                  onClick={() => setFilter(opt.v)}
                  className={cn(
                    "px-3 py-1 text-xs rounded-full border transition-colors",
                    filter === opt.v
                      ? "bg-primary text-white border-primary"
                      : "bg-background border-border hover:border-primary"
                  )}
                >
                  {opt.l}
                </button>
              ))}
            </div>

            <div className="flex flex-wrap gap-3 mb-4 p-3 bg-secondary/40 rounded-lg">
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium">Set all boys to $</span>
                <input
                  type="number"
                  value={bulkBoysPrice}
                  onChange={(e) => setBulkBoysPrice(e.target.value)}
                  className="w-20 h-7 px-2 text-xs border border-input rounded"
                />
                <button
                  onClick={applyBulkBoys}
                  className="text-xs px-2 py-1 bg-primary text-white rounded"
                >
                  Apply
                </button>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium">Set all girls to $</span>
                <input
                  type="number"
                  value={bulkGirlsPrice}
                  onChange={(e) => setBulkGirlsPrice(e.target.value)}
                  className="w-20 h-7 px-2 text-xs border border-input rounded"
                />
                <button
                  onClick={applyBulkGirls}
                  className="text-xs px-2 py-1 bg-primary text-white rounded"
                >
                  Apply
                </button>
              </div>
            </div>

            <div className="border border-border rounded-lg overflow-hidden">
              <div className="grid grid-cols-[1.4fr_0.5fr_1fr_0.8fr_0.5fr_1fr_0.8fr] gap-2 px-3 py-2 bg-secondary text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                <div>Division</div>
                <div className="text-center">Boys</div>
                <div>Boys Price</div>
                <div>Boys Max</div>
                <div className="text-center">Girls</div>
                <div>Girls Price</div>
                <div>Girls Max</div>
              </div>
              <div className="divide-y divide-border">
                {filteredRows.map(({ catalog: c, settings }) => {
                  const s = settings ?? emptySettings(event.id || "", c.id, c.display_order);
                  return (
                    <div key={c.id}>
                      <div className="grid grid-cols-[1.4fr_0.5fr_1fr_0.8fr_0.5fr_1fr_0.8fr] gap-2 px-3 py-2 items-center text-sm">
                        <div className="font-medium">{c.label}</div>
                        <div className="text-center">
                          <input
                            type="checkbox"
                            checked={s.has_boys}
                            onChange={(e) => updateRow(c.id, { has_boys: e.target.checked })}
                          />
                        </div>
                        <div className="relative">
                          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                            $
                          </span>
                          <input
                            type="number"
                            disabled={!s.has_boys}
                            value={s.boys_price || ""}
                            onChange={(e) =>
                              updateRow(c.id, { boys_price: parseFloat(e.target.value) || 0 })
                            }
                            className="w-full h-8 pl-5 pr-2 text-sm border border-input rounded disabled:opacity-50"
                          />
                        </div>
                        <div>
                          <input
                            type="number"
                            placeholder="No limit"
                            disabled={!s.has_boys}
                            value={s.boys_max_teams ?? ""}
                            onChange={(e) =>
                              updateRow(c.id, {
                                boys_max_teams: e.target.value ? parseInt(e.target.value) : null,
                              })
                            }
                            className="w-full h-8 px-2 text-sm border border-input rounded disabled:opacity-50"
                          />
                        </div>
                        <div className="text-center">
                          <input
                            type="checkbox"
                            checked={s.has_girls}
                            onChange={(e) => updateRow(c.id, { has_girls: e.target.checked })}
                          />
                        </div>
                        <div className="relative">
                          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                            $
                          </span>
                          <input
                            type="number"
                            disabled={!s.has_girls}
                            value={s.girls_price || ""}
                            onChange={(e) =>
                              updateRow(c.id, { girls_price: parseFloat(e.target.value) || 0 })
                            }
                            className="w-full h-8 pl-5 pr-2 text-sm border border-input rounded disabled:opacity-50"
                          />
                        </div>
                        <div>
                          <input
                            type="number"
                            placeholder="No limit"
                            disabled={!s.has_girls}
                            value={s.girls_max_teams ?? ""}
                            onChange={(e) =>
                              updateRow(c.id, {
                                girls_max_teams: e.target.value ? parseInt(e.target.value) : null,
                              })
                            }
                            className="w-full h-8 px-2 text-sm border border-input rounded disabled:opacity-50"
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <p className="text-xs text-muted-foreground mt-3">
              Don't see a division you need?{" "}
              <Link to="/admin-dashboard/divisions" className="text-primary hover:underline">
                Manage divisions in catalog →
              </Link>
            </p>
          </>
        )}
      </SubSection>
    </div>
  );
}
